import React, { useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFitStore } from '../store/useFitStore';
import type { CheckIn, Session } from '../store/types';
import { fmtSets } from '../lib/sets';
import NoteContent from '../components/shared/NoteContent';
import SessionEditor, { defaultSections } from '../components/coach/SessionEditor';

// Today in local time as yyyy-mm-dd (no UTC drift) — the default log date.
const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Inclusive 7-day window starting at an ISO date (a check-in week → its workouts).
const within7 = (date: string, weekStart: string): boolean => {
  if (date < weekStart) return false;
  const [y, m, d] = weekStart.split('-').map(Number);
  const end = new Date(y, m - 1, d + 6);
  const endIso = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
  return date <= endIso;
};
const exerciseCount = (s: Session): number => s.sections.reduce((n, sec) => n + sec.exercises.length, 0);

/* ---------- date helpers ---------- */
const fmtDate = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const fmtShort = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/* ---------- inline trend chart (no dependency) ----------
   Stock-app scrubbing: drag a finger (or move the mouse) across the chart and a
   crosshair snaps to the nearest week, with the value + date reading out above.
   At rest the readout shows the latest data point. */
const TrendChart: React.FC<{
  values: (number | null | undefined)[];
  labels?: string[];          // ISO dates aligned with values (the week of each point)
  unit?: string;              // "lb", "%", …
  color?: string;
}> = ({ values, labels, unit = '', color = 'rgba(255,255,255,0.9)' }) => {
  const [sel, setSel] = useState<number | null>(null); // index into pts while scrubbing
  const pts = values.map((v, i) => ({ v, i })).filter((p) => p.v != null) as { v: number; i: number }[];
  if (pts.length < 2) return <p className="text-[12px] text-fit-muted">Not enough data to chart.</p>;
  const W = 720, H = 140, pad = 10;
  const xs = pts.map((p) => p.i), ys = pts.map((p) => p.v);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const sx = (i: number) => pad + (maxX === minX ? 0 : (i - minX) / (maxX - minX)) * (W - 2 * pad);
  const sy = (v: number) => H - pad - (maxY === minY ? 0.5 : (v - minY) / (maxY - minY)) * (H - 2 * pad);
  const line = pts.map((p, k) => `${k ? 'L' : 'M'}${sx(p.i).toFixed(1)},${sy(p.v).toFixed(1)}`).join(' ');
  const area = `${line} L${sx(pts[pts.length - 1].i).toFixed(1)},${H - pad} L${sx(pts[0].i).toFixed(1)},${H - pad} Z`;

  // Map a pointer x-position to the nearest data point.
  const scrub = (e: React.PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const xData = minX + frac * (maxX - minX);
    let best = 0;
    pts.forEach((p, k) => { if (Math.abs(p.i - xData) < Math.abs(pts[best].i - xData)) best = k; });
    setSel(best);
  };

  const active = sel != null ? pts[sel] : pts[pts.length - 1];
  const activeDate = labels?.[active.i];
  // First/last points sit at the chart edges — clamp their crosshair label inward.
  const delta = sel != null && sel > 0 ? active.v - pts[sel - 1].v : null;

  return (
    <div>
      {/* readout — latest at rest, scrubbed point while touching */}
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <p className="text-[15px] font-bold tabular-nums leading-none">
          {active.v}{unit && <span className="text-[11px] font-semibold text-fit-text-secondary"> {unit}</span>}
          {delta != null && (
            <span className="text-[11px] font-semibold text-fit-muted"> {delta >= 0 ? '+' : ''}{Math.round(delta * 10) / 10} wk</span>
          )}
        </p>
        {activeDate && <p className="text-[11px] text-fit-muted tabular-nums whitespace-nowrap">{fmtDate(activeDate)}</p>}
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="w-full"
        style={{ height: 140, touchAction: 'none', cursor: 'crosshair' }}
        onPointerDown={scrub}
        onPointerMove={(e) => (e.buttons > 0 || e.pointerType === 'mouse') && scrub(e)}
        onPointerUp={() => setSel(null)}
        onPointerLeave={() => setSel(null)}
        onPointerCancel={() => setSel(null)}
      >
        <path d={area} fill="rgba(255,255,255,0.06)" />
        <path d={line} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" />
        {pts.map((p) => <circle key={p.i} cx={sx(p.i)} cy={sy(p.v)} r={2} fill={color} />)}
        {sel != null && (
          <>
            <line x1={sx(active.i)} y1={0} x2={sx(active.i)} y2={H} stroke="rgba(255,255,255,0.35)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
            <circle cx={sx(active.i)} cy={sy(active.v)} r={4} fill="#fff" />
          </>
        )}
      </svg>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <div className="card" style={{ padding: '14px 16px' }}>
    <p className="mono-label mb-1">{label}</p>
    <p className="text-2xl font-bold tabular-nums leading-none">{value}</p>
    {sub && <p className="text-[12px] text-fit-text-secondary mt-1">{sub}</p>}
  </div>
);

const ClientLog: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { user, clients, checkIns, sessions } = useFitStore();
  const client = clients.find((c) => c.id === clientId);

  // Check-ins oldest → newest (so the chart reads left-to-right in time).
  const entries = useMemo<CheckIn[]>(
    () => checkIns.filter((c) => c.clientId === clientId).sort((a, b) => a.weekOf.localeCompare(b.weekOf)),
    [checkIns, clientId]
  );
  // Workout sessions newest → oldest (the day-by-day log).
  const workouts = useMemo<Session[]>(
    () => sessions.filter((s) => s.clientId === clientId).sort((a, b) => b.date.localeCompare(a.date)),
    [sessions, clientId]
  );

  const [open, setOpen] = useState<Set<string>>(new Set());
  const [highlightWeek, setHighlightWeek] = useState<string | null>(null);
  const [showAllWeeks, setShowAllWeeks] = useState(false);
  const [logDate, setLogDate] = useState<string>(todayIso());
  const [editor, setEditor] = useState<{ session: Session; isNew: boolean } | null>(null);
  const workoutRef = useRef<HTMLDivElement>(null);

  // Start logging a brand-new session for the chosen date (live, sheet-style entry
  // with the exercise-library picker). coachId must be the signed-in coach.
  const startLog = () => {
    if (!user || !clientId) return;
    setEditor({
      isNew: true,
      session: {
        id: crypto.randomUUID(),
        clientId,
        coachId: user.id,
        date: logDate,
        title: '',
        status: 'completed', // logging real-time → already done
        sections: defaultSections(),
      },
    });
  };
  const toggle = (id: string) => setOpen((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  // Clicking a weekly check-in jumps to that week's workouts and expands them.
  // Scroll to the first matched session row (the list is newest-first, so that's
  // the top of the week's cluster) — not the section header 250 rows above it.
  const focusWeek = (weekOf: string) => {
    const ids = workouts.filter((w) => within7(w.date, weekOf)).map((w) => w.id);
    setHighlightWeek(weekOf);
    setOpen((prev) => new Set([...prev, ...ids]));
    requestAnimationFrame(() => {
      const target = ids.length ? document.getElementById(`sess-${ids[0]}`) : null;
      (target || workoutRef.current)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  if (!client) {
    return (
      <div className="main-content">
        <button onClick={() => navigate('/coach')} className="text-fit-muted hover:text-white text-[13px]">← Clients</button>
        <p className="mt-6">Client not found.</p>
      </div>
    );
  }

  const first = entries[0];
  const latest = entries[entries.length - 1];
  const delta = (cur?: number | null, start?: number | null) =>
    cur != null && start != null ? `${cur - start >= 0 ? '+' : ''}${(cur - start).toFixed(1)}` : '';
  const lastSmm = [...entries].reverse().find((e) => e.skeletalMuscleMass != null)?.skeletalMuscleMass;
  const firstSmm = entries.find((e) => e.skeletalMuscleMass != null)?.skeletalMuscleMass;

  return (
    <div className="main-content" style={{ maxWidth: 960 }}>
      <button onClick={() => navigate('/coach')} className="text-fit-muted hover:text-white text-[13px] mb-3">← Clients</button>
      <header className="flex items-end justify-between gap-3 flex-wrap mb-6">
        <div>
          <p className="mono-label">Training Log</p>
          <h1 className="text-3xl font-bold tracking-tight">{client.name}</h1>
        </div>
        <p className="text-[13px] text-fit-text-secondary">
          {entries.length} check-in{entries.length === 1 ? '' : 's'}
          {first && latest && <> · {fmtDate(first.weekOf)} – {fmtDate(latest.weekOf)}</>}
        </p>
      </header>

      {/* ---- Client info ---- */}
      <section className="mb-8">
        <h2 className="mono-label mb-3">Client info</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Weight" value={latest?.weightLbs != null ? `${latest.weightLbs} lb` : '—'} sub={first?.weightLbs != null ? `${delta(latest?.weightLbs, first?.weightLbs)} from start` : undefined} />
          <StatCard label="Body fat" value={latest?.bodyFatPct != null ? `${latest.bodyFatPct}%` : '—'} sub={first?.bodyFatPct != null ? `${delta(latest?.bodyFatPct, first?.bodyFatPct)} pts from start` : undefined} />
          <StatCard label="Muscle (lb)" value={lastSmm != null ? `${lastSmm}` : '—'} sub={firstSmm != null ? `${delta(lastSmm, firstSmm)} from start` : undefined} />
          <StatCard label="Started" value={client.startDate ? fmtShort(client.startDate) : '—'} sub={client.lifecycle ? client.lifecycle : undefined} />
        </div>
        {client.goals && (
          <div className="card mt-3" style={{ padding: '14px 16px' }}>
            <p className="mono-label mb-1">Goals & baseline</p>
            <p className="text-[14px] leading-relaxed">{client.goals}</p>
          </div>
        )}
        {client.injuries && (
          <div className="card mt-3" style={{ padding: '14px 16px' }}>
            <p className="mono-label mb-1">Injuries / limitations</p>
            <p className="text-[14px] leading-relaxed">{client.injuries}</p>
          </div>
        )}
      </section>

      {/* ---- Progress charts ---- */}
      {entries.length >= 2 && (
        <section className="mb-8" data-tour="trend">
          <h2 className="mono-label mb-3">Progress</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="card" style={{ padding: '14px 16px' }}>
              <p className="text-[13px] font-semibold mb-2">Weight (lb)</p>
              <TrendChart values={entries.map((e) => e.weightLbs)} labels={entries.map((e) => e.weekOf)} unit="lb" />
            </div>
            <div className="card" style={{ padding: '14px 16px' }}>
              <p className="text-[13px] font-semibold mb-2">Body fat (%)</p>
              <TrendChart values={entries.map((e) => e.bodyFatPct)} labels={entries.map((e) => e.weekOf)} unit="%" color="rgba(255,255,255,0.55)" />
            </div>
            <div className="card" style={{ padding: '14px 16px' }}>
              <p className="text-[13px] font-semibold mb-2">Muscle (lb)</p>
              <TrendChart values={entries.map((e) => e.skeletalMuscleMass)} labels={entries.map((e) => e.weekOf)} unit="lb" color="rgba(255,255,255,0.55)" />
            </div>
          </div>
        </section>
      )}

      {/* ---- Weekly check-ins table ---- */}
      <section className="mb-8">
        <h2 className="mono-label mb-3">Weekly check-ins</h2>
        {entries.length === 0 ? (
          <div className="card" style={{ padding: 'var(--spacing-lg)' }}>
            <p className="text-[14px] text-fit-text-secondary">No check-ins logged yet.</p>
          </div>
        ) : (
          <div className="card overflow-x-auto" style={{ padding: 0 }}>
            <table className="w-full text-[13px]" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr className="text-fit-muted text-left" style={{ borderBottom: '1px solid var(--line)' }}>
                  <th className="px-4 py-3 font-semibold">Week of</th>
                  <th className="px-4 py-3 font-semibold tabular-nums">Weight</th>
                  <th className="px-4 py-3 font-semibold tabular-nums">Body fat</th>
                  <th className="px-4 py-3 font-semibold tabular-nums">Muscle</th>
                  <th className="px-4 py-3 font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody>
                {/* charts above already tell the long story — default to the recent 12 weeks */}
                {[...entries].reverse().slice(0, showAllWeeks ? undefined : 12).map((e, i) => {
                  const note = [e.diet && `diet: ${e.diet}`, e.sleep && `sleep: ${e.sleep}`, e.recovery && `recovery: ${e.recovery}`, e.notes].filter(Boolean).join(' · ');
                  return (
                    <tr
                      key={e.id}
                      onClick={() => focusWeek(e.weekOf)}
                      className="cursor-pointer hover:bg-white/[0.03]"
                      title="See this week's workouts"
                      style={{ borderBottom: i === entries.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.05)' }}
                    >
                      <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(e.weekOf)}</td>
                      <td className="px-4 py-2.5 tabular-nums">{e.weightLbs != null ? `${e.weightLbs} lb` : '—'}</td>
                      <td className="px-4 py-2.5 tabular-nums">{e.bodyFatPct != null ? `${e.bodyFatPct}%` : '—'}</td>
                      <td className="px-4 py-2.5 tabular-nums">{e.skeletalMuscleMass != null ? `${e.skeletalMuscleMass}` : '—'}</td>
                      <td className="px-4 py-2.5 text-fit-text-secondary">{note || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {entries.length > 12 && (
              <button
                onClick={() => setShowAllWeeks((v) => !v)}
                className="w-full text-[12px] text-fit-text-secondary hover:text-white py-2.5"
                style={{ borderTop: '1px solid var(--line)' }}
              >
                {showAllWeeks ? 'Show recent 12 weeks' : `Show all ${entries.length} weeks ↓`}
              </button>
            )}
          </div>
        )}
      </section>

      {/* ---- Nutrition ---- */}
      {client.coachingNotes && client.coachingNotes.length > 0 && (
        <section className="mb-8">
          <h2 className="mono-label mb-3">Nutrition & notes</h2>
          <div className="flex flex-col gap-3">
            {client.coachingNotes.map((n) => (
              <div key={n.id} className="card" style={{ padding: '14px 16px' }}>
                <NoteContent text={n.text} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---- Workout log (day by day, mirrors the sheet) ---- */}
      <section className="mb-8" ref={workoutRef}>
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h2 className="mono-label">Workout log</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {highlightWeek && (
              <button onClick={() => setHighlightWeek(null)} className="text-[12px] text-fit-muted hover:text-white">Clear week filter</button>
            )}
            <span className="text-[12px] text-fit-text-secondary">{workouts.length} session{workouts.length === 1 ? '' : 's'}</span>
            <input
              type="date"
              value={logDate}
              onChange={(e) => setLogDate(e.target.value)}
              className="bg-fit-bg-tertiary border border-fit-line rounded-md px-2 py-1 text-[12px] tabular-nums outline-none focus:border-fit-line-strong"
              aria-label="Log date"
            />
            <button onClick={startLog} className="btn btn-primary btn-sm">＋ Log session</button>
          </div>
        </div>
        {workouts.length === 0 ? (
          <div className="card" style={{ padding: 'var(--spacing-lg)' }}>
            <p className="text-[14px] text-fit-text-secondary">No workouts logged yet.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {workouts.map((w) => {
              const isOpen = open.has(w.id);
              const inFocus = highlightWeek != null && within7(w.date, highlightWeek);
              return (
                <div key={w.id} id={`sess-${w.id}`} className="card" style={{ padding: 0, borderColor: inFocus ? 'var(--line-strong)' : undefined, scrollMarginTop: 70 }}>
                  <button
                    onClick={() => toggle(w.id)}
                    className="w-full flex items-center justify-between gap-3 text-left"
                    style={{ padding: '12px 16px' }}
                  >
                    <div className="flex items-baseline gap-3 min-w-0">
                      <span className="text-[13px] font-semibold tabular-nums whitespace-nowrap" style={{ minWidth: 116 }}>{fmtDate(w.date)}</span>
                      <span className="text-[14px] font-semibold truncate">{w.title}</span>
                    </div>
                    <span className="text-[12px] text-fit-text-secondary shrink-0">{exerciseCount(w)} ex · {isOpen ? '−' : '+'}</span>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 flex flex-col gap-4" style={{ borderTop: '1px solid var(--line)' }}>
                      <button
                        onClick={() => setEditor({ session: w, isNew: false })}
                        className="self-end text-[12px] text-fit-text-secondary hover:text-white mt-2"
                      >
                        Edit / log ↗
                      </button>
                      {w.sections.map((sec) => (
                        <div key={sec.id}>
                          <p className="mono-label mb-2 mt-3">{sec.title}</p>
                          <div className="flex flex-col gap-1.5">
                            {sec.exercises.map((ex) => (
                              <div key={ex.id} className="flex items-baseline justify-between gap-3 border-b border-fit-line pb-1.5">
                                <p className="text-[13px] font-semibold">{ex.name}{ex.note && <span className="text-fit-muted font-normal"> · {ex.note}</span>}</p>
                                <p className="text-[12px] text-fit-text-secondary tabular-nums shrink-0 text-right">
                                  {fmtSets(ex.sets, ex.measure, ex.unit, '  ·  ') || '—'}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ---- live logger / editor (sheet-style entry + exercise picker) ---- */}
      {editor && (
        <SessionEditor
          session={editor.session}
          isNew={editor.isNew}
          clientName={client.name}
          onClose={() => setEditor(null)}
        />
      )}
    </div>
  );
};

export default ClientLog;
